import { SdkContext } from '../internal'
import type { AuthResponse } from './auth'

/** F-233: the 2FA of the session's account here (also `GET /auth/me` › `twoFactor`). */
export interface TwoFactorSummary {
  enabled: boolean
  /** The account must have it: the team, the SuperAdmin, or a member where the owner turned the default on. */
  required: boolean
  requiredReason: 'team' | 'superAdmin' | 'community' | null
  /** Must have it and has not turned it on yet. */
  setupRequired: boolean
  /** Team and SuperAdmin without 2FA: from this date the team routes answer 403 `TWO_FACTOR_REQUIRED`. */
  deadline: string | null
}

export interface TwoFactorStatus extends TwoFactorSummary {
  enabledAt: string | null
  /** Masked, e.g. `+5541*****8888`. */
  phone: string | null
  recoveryCodesRemaining: number
  rememberedDevices: number
  deviceDays: number
}

export type TwoFactorChannel = 'whatsapp' | 'sms'

export interface TwoFactorCodeSent {
  phone: string
  expiresAt: string
  resendAfterSeconds: number
  sentVia: TwoFactorChannel
}

/** `POST /auth/login` of an account with 2FA on: no token yet, the code step comes first. */
export interface TwoFactorChallenge {
  twoFactorRequired: true
  challengeId: string
  challengeExpiresAt?: string
  phone: string | null
  /** `null` when the code could not go out now (`codeError` says why, e.g. 429 one code a minute). */
  sentVia: TwoFactorChannel | null
  expiresAt?: string | null
  resendAfterSeconds?: number | null
  channels?: TwoFactorChannel[]
  codeError?: { status: number; message: string; retryAfterSeconds?: number } | null
}

export interface TwoFactorLoginResult extends AuthResponse {
  /** Only with `rememberDevice: true`: sent on the next logins, the code is not asked for 90 days. */
  deviceToken?: string
  recoveryCodeUsed?: boolean
  recoveryCodesRemaining?: number
}

export interface TwoFactorDevice {
  id: string
  browser: string | null
  os: string | null
  createdAt: string
  lastUsedAt: string
  expiresAt: string
}

export interface TwoFactorEvent {
  id: string
  type: string
  createdAt: string
  byPlatform?: boolean
}

export class TwoFactorResource {
  constructor(private ctx: SdkContext) {}

  /* ------------------------------------------- the login's second step -- */

  /** The code (or a recovery code) ends the login; the answer is the login's. Public. */
  async verifyLogin(body: { challengeId: string; code?: string; recoveryCode?: string; rememberDevice?: boolean }): Promise<TwoFactorLoginResult> {
    return this.ctx.callKeepingRetryAfter<TwoFactorLoginResult>({ httpMethod: 'POST', path: '/auth/login/two-factor', hasBody: true }, { body })
  }

  /** A new login code; `channel: 'sms'` is "Receber por SMS". A 429 keeps `retryAfter`. */
  async resendLogin(challengeId: string, channel?: TwoFactorChannel): Promise<TwoFactorCodeSent> {
    return this.ctx.callKeepingRetryAfter<TwoFactorCodeSent>(
      { httpMethod: 'POST', path: '/auth/login/two-factor/resend', hasBody: true },
      { body: { challengeId, ...(channel ? { channel } : {}) } }
    )
  }

  /* -------------------------------------------- Minha conta › Segurança -- */

  async status(): Promise<TwoFactorStatus> {
    return this.ctx.call<TwoFactorStatus>({ httpMethod: 'GET', path: '/auth/two-factor' })
  }

  /** Sends the setup code (2FA on: changes its phone, behind the step-up). */
  async startSetup(body: { phone?: string; phoneCountry?: string; channel?: TwoFactorChannel }): Promise<TwoFactorCodeSent> {
    return this.ctx.call<TwoFactorCodeSent>({ httpMethod: 'POST', path: '/auth/two-factor/setup', hasBody: true }, { body })
  }

  /** The first time, `recoveryCodes` holds the 10 codes, shown only now. */
  async confirmSetup(code: string): Promise<{ enabled: boolean; phone: string; phoneChanged?: boolean; recoveryCodes?: string[] }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/auth/two-factor/setup/confirm', hasBody: true }, { body: { code } })
  }

  async disable(): Promise<{ enabled: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/auth/two-factor' })
  }

  async regenerateRecoveryCodes(): Promise<{ recoveryCodes: string[] }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/auth/two-factor/recovery-codes', hasBody: true }, { body: {} })
  }

  async requestStepUp(channel?: TwoFactorChannel): Promise<TwoFactorCodeSent> {
    return this.ctx.call<TwoFactorCodeSent>({ httpMethod: 'POST', path: '/auth/two-factor/step-up', hasBody: true }, { body: channel ? { channel } : {} })
  }

  async verifyStepUp(body: { code?: string; recoveryCode?: string }): Promise<{ stepUpToken: string; expiresAt: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/auth/two-factor/step-up/verify', hasBody: true }, { body })
  }

  async devices(): Promise<TwoFactorDevice[]> {
    const answer = await this.ctx.call<TwoFactorDevice[] | { items?: TwoFactorDevice[] }>({ httpMethod: 'GET', path: '/auth/two-factor/devices' })
    return Array.isArray(answer) ? answer : answer?.items || []
  }

  async revokeDevice(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/auth/two-factor/devices/{id}' }, { id })
  }

  async revokeAllDevices(): Promise<{ removed: number }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/auth/two-factor/devices' })
  }

  async events(limit = 20): Promise<TwoFactorEvent[]> {
    const answer = await this.ctx.call<TwoFactorEvent[] | { items?: TwoFactorEvent[] }>({ httpMethod: 'GET', path: '/auth/two-factor/events' }, { query: { limit } })
    return Array.isArray(answer) ? answer : answer?.items || []
  }

  /* ------------------------------------------------------- SuperAdmin -- */

  async adminStatus(memberId: string): Promise<TwoFactorStatus> {
    return this.ctx.call<TwoFactorStatus>({ httpMethod: 'GET', path: '/admin/members/{memberId}/two-factor' }, { memberId })
  }

  async adminReset(memberId: string, reason: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/members/{memberId}/two-factor/reset', hasBody: true }, { memberId, body: { reason } })
  }
}
